import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpShortWide, faArrowDownWideShort } from '@fortawesome/free-solid-svg-icons';
import { OrderDirection } from '../../Models/Models';

const DEFAULT_PAGE_SIZES = [6, 12, 24, 48];

interface TableToolbarFilterOption {
    value: string;
    label: string;
}

interface TableToolbarFilter {
    label: string;
    options: TableToolbarFilterOption[];
    value: string;
    onChange: (value: string) => void;
}

interface TableToolbarProps {
    totalCount: number;
    /** Slovak plural forms: [1, 2–4, 5+], e.g. ['príspevok', 'príspevky', 'príspevkov'] */
    nounForms: [string, string, string];
    perPage: number;
    onPerPageChange: (count: number) => void;
    pageSizes?: number[];
    orderDirection?: OrderDirection;
    onOrderChange?: (dir: OrderDirection) => void;
    filter?: TableToolbarFilter;
}

const TableToolbar: React.FC<TableToolbarProps> = ({
    totalCount,
    nounForms,
    perPage,
    onPerPageChange,
    pageSizes = DEFAULT_PAGE_SIZES,
    orderDirection,
    onOrderChange,
    filter,
}) => {
    const countLabel =
        totalCount === 1
            ? `1 ${nounForms[0]}`
            : totalCount >= 2 && totalCount <= 4
                ? `${totalCount} ${nounForms[1]}`
                : `${totalCount} ${nounForms[2]}`;

    // Only offer sizes below the total, plus the first size that covers everything;
    // keep the currently selected size so the select never shows a missing value.
    const sizeOptions = pageSizes.filter(s => s < totalCount);
    const covering = pageSizes.find(s => s >= totalCount);
    if (covering !== undefined) sizeOptions.push(covering);
    if (!sizeOptions.includes(perPage)) sizeOptions.push(perPage);
    sizeOptions.sort((a, b) => a - b);

    return (
        <>
            <div className="st-table-toolbar-group">
                <span className="st-mono st-table-toolbar-count">
                    Spolu {countLabel}
                </span>
                {sizeOptions.length > 1 && (
                    <label className="st-table-toolbar-label">
                        Na stránku
                        <select
                            className="form-select form-select-sm st-table-toolbar-select"
                            value={perPage}
                            onChange={e => onPerPageChange(Number(e.target.value))}
                        >
                            {sizeOptions.map(n => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                    </label>
                )}
            </div>
            <div className="st-table-toolbar-group">
                {filter && (
                    <div className="st-table-toolbar-label">
                        {filter.label}
                        <div className="st-segmented" role="group" aria-label={filter.label}>
                            {filter.options.map(option => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`st-segment${filter.value === option.value ? ' st-segment--active' : ''}`}
                                    aria-pressed={filter.value === option.value}
                                    onClick={() => {
                                        if (filter.value !== option.value) filter.onChange(option.value);
                                    }}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {orderDirection !== undefined && onOrderChange && (
                    <div className="st-table-toolbar-label">
                        Zoradenie
                        <div className="st-segmented" role="group" aria-label="Zoradenie">
                            <button
                                type="button"
                                className={`st-segment${orderDirection === 'Desc' ? ' st-segment--active' : ''}`}
                                aria-pressed={orderDirection === 'Desc'}
                                onClick={() => {
                                    if (orderDirection !== 'Desc') onOrderChange('Desc');
                                }}
                            >
                                <FontAwesomeIcon icon={faArrowDownWideShort} className="st-segment__icon"/>
                                Najnovšie
                            </button>
                            <button
                                type="button"
                                className={`st-segment${orderDirection === 'Asc' ? ' st-segment--active' : ''}`}
                                aria-pressed={orderDirection === 'Asc'}
                                onClick={() => {
                                    if (orderDirection !== 'Asc') onOrderChange('Asc');
                                }}
                            >
                                <FontAwesomeIcon icon={faArrowUpShortWide} className="st-segment__icon"/>
                                Najstaršie
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default TableToolbar;
